/**
 * @file element-groups.ts
 * @description Сводки и состояния групп панели элемента секции (phase19.16,
 * SB122; uiux-settings-groups §4.3, §6; SB120 этап 3а — uiux-sb120-stage3
 * §2.1): «Цвет и шрифт» — «свой #F4EBDD · Lora 56» / «авто · шрифт
 * заголовков», значки «!» (контраст, мелкий H1, больше 3 шрифтов) и «✕»
 * (свой шрифт удалён) по предупреждениям shared `sectionElementWarnings`;
 * «Шрифт» — названия шрифтов; «Расположение» — «по центру · 24 / обычно ·
 * все». Подписи значений — из реестров.
 */

import {
  resolveFontRole,
  type FontPart,
  type FontPartRole,
  type FontRole,
  type FontRoleTheme,
  type SectionElement,
  type SectionElementWarning,
  type SiteFontRegistry,
  type TypoValue,
} from "@chaiberi/shared";

import { devicesSummary, showFromHide } from "./devices";
import { fontLineLabel, fontLineName, type FontNoteLine } from "./element-font-lines";
import { formatNumber } from "../site-fields/number-input";
import { TEXT_COLOR_ROLE_LABELS } from "../site-fields/text-color-labels";

/** Выравнивание элемента словами (сводка «Расположения»). */
export const ALIGN_WORD: Readonly<Record<NonNullable<SectionElement["align"]> | "auto", string>> = {
  auto: "как колонка",
  left: "влево",
  center: "по центру",
  right: "вправо",
};

/** Отступ сводкой: px или «обычно» (ритм колонки). */
const spaceWord = (px: number | undefined): string => (px === undefined ? "обычно" : formatNumber(px, 2));

/** «по центру · 24 / обычно · все» — выравнивание, отступы сверху / снизу, устройства. */
export function placementSummary(element: SectionElement): string {
  const { top, bottom } = element.space ?? {};
  const space = top === undefined && bottom === undefined ? "обычно" : `${spaceWord(top)} / ${spaceWord(bottom)}`;
  return `${ALIGN_WORD[element.align ?? "auto"]} · ${space} · ${devicesSummary(showFromHide(element.hide))}`;
}

/** Роль шрифта словами для сводки. */
const ROLE_WORD: Readonly<Record<FontRole, string>> = {
  heading: "шрифт заголовков",
  body: "основной шрифт",
  accent: "шрифт акцента",
};

/**
 * Сводка «Цвета и шрифта» по первой части со своими настройками (иначе —
 * по первой): «Название: свой #F4EBDD · Lora 56», «авто · шрифт заголовков».
 */
export function typoSummary(parts: readonly FontPart[], typo: TypoValue | undefined, theme: FontRoleTheme, registry: SiteFontRegistry): string {
  const chosen = parts.find((p) => p.key !== undefined && typo?.[p.key] !== undefined) ?? parts[0];
  if (!chosen?.key) return "";
  const part = typo?.[chosen.key];
  const c = part?.color;
  const color = !c ? "авто" : c.kind === "custom" ? `свой ${c.color.toUpperCase()}` : TEXT_COLOR_ROLE_LABELS[c.role].toLowerCase();
  const f = part?.font;
  const role = (r: FontPartRole) => ROLE_WORD[resolveFontRole(r, theme)];
  const font = !f ? role(chosen.role) : f.kind === "custom" ? (registry.find(f.font)?.family ?? "свой шрифт") : role(f.role === "auto" ? chosen.role : f.role);
  const size = part?.size !== undefined ? ` ${formatNumber(part.size, 2)}` : "";
  const prefix = parts.length > 1 && chosen.part ? `${chosen.part}: ` : "";
  return `${prefix}${color} · ${font}${size}`;
}

const isUnder = (path: string, prefix: string): boolean => path === prefix || path.startsWith(`${prefix}.`);

/**
 * Значок группы по предупреждениям под путями полей группы: удалённый свой
 * шрифт — «✕» (error), остальное — «!» (warning); замечание проверки — «✕».
 */
export function typoStatus(warnings: readonly SectionElementWarning[], prefixes: readonly string[], invalid = false): "error" | "warning" | undefined {
  if (invalid) return "error";
  const own = warnings.filter((w) => prefixes.some((p) => isUnder(w.path, p)));
  if (own.some((w) => w.code === "element_font_missing")) return "error";
  return own.length > 0 ? "warning" : undefined;
}

/** Сводка «Шрифта»: названия шрифтов частей элемента, без повторов. */
export function fontSummary(lines: readonly FontNoteLine[], theme: FontRoleTheme): string {
  return [...new Set(lines.map((line) => fontLineName(line) ?? fontLineLabel(line, theme)))].join(" · ");
}
